import { identifyMixpanelUser } from '@/analytics/identifyMixpanelUser'
import { withMixpanel } from '@/analytics/withMixpanel'
import { useAnalytics } from '@/composables/useAnalytics'
import { resetClient } from '@/composables/useClient'
import { accountQueries } from '@/queries/account'
import { dataDomains } from '@/queries/dataDomains'
import { authMutations } from '@/queries/auth'
import { HttpError } from '@/queries/auth-error'
import { queryClient } from '@/queries/queryClient'
import { isTokenExpired } from '@/utils/isTokenExpired'
import { refreshSessionOnce } from '@/utils/refreshSession'
import dayjs from 'dayjs'
import { jwtDecode } from 'jwt-decode'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import flagsmith from 'flagsmith'

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>()
  const isAuthenticated = computed(() => !!accessToken.value)
  const lastTokenCheck = ref(0)
  const getAccessToken = async (): Promise<string | undefined> => {
    return accessToken.value || undefined
  }

  const identifyUser = async (): Promise<void> => {
    try {
      const user = await queryClient.fetchQuery(accountQueries.profile())

      if (!user.email) {
        return
      }

      // Flagsmith identify is best-effort — a rejection here shouldn't skip the
      // preload/analytics identify below, which don't depend on it.
      await flagsmith.identify(user.cognitoUserId, { email: user.email }).catch((error) => {
        console.error('Failed to identify Flagsmith user', error)
      })

      // Preload the account-level data the People profile (and super properties) read from,
      // using the same ensureQueryData primitive as ensureRouteData — a one-off fetch that
      // skips already-cached domains, so token refresh doesn't refetch them. Failures are
      // swallowed; the People profile just omits whichever slice failed.
      await Promise.allSettled([
        queryClient.ensureQueryData(dataDomains.subscriptions()),
        queryClient.ensureQueryData(dataDomains.dogs()),
        queryClient.ensureQueryData(accountQueries.defaultAddress()),
      ])

      useAnalytics().identify({
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
        phone: user.phoneNumber,
        tenant: user.tenantType!,
        cognito_user_id: user.cognitoUserId,
      })

      withMixpanel((mixpanel) => {
        identifyMixpanelUser(mixpanel)
        // Flush immediately so Mixpanel identify + People.set run synchronously at login,
        // not 500ms later — Segment/CIO identify above is synchronous, and a session that
        // ends before the trailing timer fires would otherwise never identify to Mixpanel.
        // The debounce still coalesces any later cache-driven bursts.
        identifyMixpanelUser.flush()
      })
    } catch (error) {
      console.error('Failed to identify analytics user', error)
    }
  }

  const resetAnalytics = (): void => {
    useAnalytics().reset()

    withMixpanel((mixpanel) => {
      mixpanel.reset()
      mixpanel.stop_session_recording()
    })
  }

  const setAccessToken = (token: string): void => {
    accessToken.value = token
  }

  const removeAccessToken = async (): Promise<void> => {
    accessToken.value = null
    resetAnalytics()

    // Best-effort — a rejection here shouldn't stop logout()'s finally block
    // from reaching resetClient().
    await flagsmith.logout().catch((error) => {
      console.error('Failed to log out Flagsmith user', error)
    })
  }

  const refreshToken = async (): Promise<void> => {
    try {
      const response = flagsmith.hasFeature('feature/auth_session_bootstrap')
        ? await refreshSessionOnce()
        : await authMutations.refresh().mutationFn()
      setAccessToken(response.accessToken)
      await identifyUser()
    } catch (error) {
      if (error instanceof HttpError) {
        await removeAccessToken()
        return
      }

      throw error
    }
  }

  const restoreSessionFromCookie = async (): Promise<boolean> => {
    try {
      const { accessToken: token } = await authMutations.session().mutationFn()
      setAccessToken(token)
      await identifyUser()
      return true
    } catch (error) {
      if (error instanceof HttpError) return false

      throw error
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await authMutations.logout().mutationFn()
    } finally {
      if (isAuthenticated.value) {
        useAnalytics().track('Signed Out')
      }

      queryClient.clear()
      await removeAccessToken()
      resetClient()
    }
  }

  const checkIfTokenNeedRefresh = async (): Promise<void> => {
    const token = accessToken.value

    if (!token || isTokenExpired(token)) {
      console.debug("No access token or it's expired, attempting to restore from cookie")
      if (flagsmith.hasFeature('feature/auth_session_bootstrap')) {
        if (!(await restoreSessionFromCookie())) {
          await refreshToken()
        }
      } else {
        await refreshToken()
      }
    }
  }

  const checkIfTokenExpired = async (): Promise<void> => {
    const token = accessToken.value
    if (!token) return

    const now = dayjs()

    try {
      const decoded = jwtDecode<{ exp: number }>(token)
      const expired = dayjs.unix(decoded.exp)
      const expiredMinusTwoMin = expired.subtract(2, 'minute')

      if (dayjs().isAfter(expiredMinusTwoMin)) {
        console.debug('Access token is expired, refreshing token...')
        await refreshToken()
      }
    } catch {
      console.error('Failed to decode access token')
    }
    lastTokenCheck.value = now.valueOf()
  }

  return {
    isAuthenticated,
    checkIfTokenNeedRefresh,
    checkIfTokenExpired,
    getAccessToken,
    setAccessToken,
    identifyUser,
    removeAccessToken,
    refreshToken,
    logout,
  }
})
